import convertDate from './convertDate';
import { getBreedName, getLocality } from './httpRequest';

//Classe représentant une adresse
export class Address {
    #locality = {
        id: null,
        label: null,
        postal_code: null
    }
    #country = {
        id: null,
        label: null
    }

    constructor(street = null, street_nb = null, postal_box = null, locality_id = null) {
        this.street = street;
        this.street_nb = street_nb;
        this.postal_box = postal_box;
        this.#locality.id = locality_id;
    }

    getCompleteStreetString() {
        if(this.street && this.street_nb) return `${this.street}, ${this.street_nb}${this.postal_box || ""}`;
        else return "";
    }

    static fromJSON(json) {
        return new Address(json.street, json.street_nb, json.postal_box, json.locality_id);
    }

    getJSON() {
        return {
            street: this.street,
            street_nb: this.street_nb,
            postal_box: this.postal_box,
            locality_id: this.#locality.id
        }
    }

    async getLocality() {
        if (this.#locality.id && this.#locality.postal_code && this.#locality.label) return this.#locality;
        else if (this.#locality.id) {
            const locality = await getLocality(this.#locality.id);
            this.#locality.label = locality.label;
            this.#locality.postal_code = locality.postal_code;
            this.#country.id = locality.country;
            return this.#locality;
        } else {
            return "";
        }
    }

    getLocalitySync(localities) {
        if(localities)
        {
            return localities.find((locality) => this.#locality.id === locality.id);
        } else {
            return this.#locality
        }
        
    }

    getCountry(countries) {
        if (this.#country.id && this.#country.label) {
            return this.#country;
        } else if (this.#country.id) {
            const country = countries.find((country) => country.id === this.#country.id);
            if (country) this.#country.label = country.label;
            return this.#country || "";
        }
        else {
            return "";
        }
    }

    setCountry(country) {
        if (country.label && country.id){
            this.#country.label = country.label;
            this.#country.id = country.id;
        } 
        else console.warn("Invalid country format");
    }
    getCountryId() {
        return this.#country.id || "";
    }

    setPostalCode(postal_code) {
        this.#locality.postal_code = postal_code;
    }

    getLocalityId() {
        return this.#locality.id || "";
    }
    async getPostalCode() {
        if (this.#locality.postal_code) return this.#locality.postal_code;
        if (!this.#locality.id) {
            return "";
        } else {
            const locality = await getLocality(this.#locality.id);
            this.#locality.label = locality.label;
            this.#locality.postal_code = locality.postal_code;
            this.#country.id = locality.country;
            return locality.postal_code;
        }
    }

    //only return if postal code is already defined
    getPostalCodeSync() {
        return this.#locality.postal_code || "";
    }

    setCountryId(id) {
        this.#country.id = id;
    }
    setLocalityId(id) {
        this.#locality.id = id;
    }
    setLocalityLabel(label){
        this.#locality.label = label;
    }
    setLocality(locality) {
        this.#locality.id = locality.id
        this.#locality.label = locality.label
        this.#locality.postal_code = locality.postal_code
    }
}

//Classe représentant un propriétaire d'animal
export class PetOwner {
    constructor(firstname = null, name = null, email = null, phone = null, address = new Address(), company_name = null, vat = null, language = "FR") {
        if (!(address instanceof Address) || !address) {
            console.error("address must be an instance of Address");
        }
        this.language = language;
        this.firstname = firstname;
        this.name = name;
        this.email = email;
        this.phone = phone;
        this.vat = vat;
        this.address = address;
        this.company_name = company_name;
    }

    static fromJSON(json) {
        const address = Address.fromJSON(json.address)
        return new PetOwner(json.firstname, json.name, json.email, json.phone, address, json.company_name, json.vat, json.language);
    }
    getJSON() {
        return {
            firstname: this.firstname,
            name: this.name,
            email: this.email,
            phone: this.phone,
            address: this.address.getJSON(),
            company_name: this.company_name,
            vat: this.vat,
            language: this.language
        }
    }
    handleChange(name, value) {
        const object = this;
        switch (name) {
            case 'name': object.name = value; break;
            case 'firstname': object.firstname = value; break;
            case 'phone': object.phone = value.replace(/[^0-9]/g, ''); break; // Supprime tout sauf les chiffres
            case 'email': object.email = value; break;
            case 'language': object.language = value; break;
            case 'company_name': object.company_name = value; break;
            case 'vat': object.vat = value; break;
            case 'street': object.address.street = value; break;
            case 'street_nb': object.address.street_nb = value; break;
            case 'postal_box': object.address.postal_box = value; break;
            case 'country': object.address.setCountryId(value); break;
            case 'locality': object.address.setLocalityId(value); break;
            case 'locality_label': object.address.setLocalityLabel(value); break;
            case 'postal_code': object.address.setPostalCode(value); break;
            default: console.warn(`handleChange: Clé "${name}" non reconnue.`); break;
        }
        return object;
    }
}

//Classe représentant un animal
export class Pet {
    #breed_name;
    //Supprimer breed_name quand l'endpoint des races sera créé
    constructor(name=null, specie_id=null, breed_id=null, breed_name=null, birth_date=null, chip=null, sex_id=null, sterilisation_id=null) {
        this.name = name;
        this.specie_id = specie_id;
        this.breed_id = breed_id;
        this.#breed_name = breed_name;
        this.birth_date = birth_date ? this.parseDate(birth_date) : null;
        this.chip = chip;
        this.sex_id = sex_id;
        this.sterilisation_id = sterilisation_id;
    }

    static fromJSON(json) {
        return new Pet(json.name, json.specie_id, json.breed_id, json.breed_name, json.birth_date, json.chip, json.sex_id, json.sterilisation_id);
    }

    getJSON() {
        return {
            name: this.name,
            specie_id: this.specie_id,
            breed_id: this.breed_id,
            breed_name: this.#breed_name,
            birth_date: this.birth_date,
            chip: this.chip,
            sex_id: this.sex_id,
            sterilisation_id: this.sterilisation_id
        }
    }

    getSpecie(species) {
        return species.find((specie) => parseInt(specie.id) === parseInt(this.specie_id)) || "";
    }

    getSex(sexes) {
        return sexes.find((sex) => parseInt(sex.id) === parseInt(this.sex_id)) || "";
    }

    getSterilisation(sterilisations) {
        return sterilisations.find((sterilisation) => parseInt(sterilisation.id) === parseInt(this.sterilisation_id)) || "";
    }

    async getBreedName() {
        if (this.#breed_name) return this.#breed_name;
        if (!this.breed_id) {
            return "";
        } else {
            const breed = await getBreedName(this.breed_id);
            this.#breed_name = breed;
            return breed.label;
        }
    }

    //only get a breed name if already defined as a name
    getBreedNameSync() {
        return this.#breed_name || "";
    }

    parseDate(date) {
        if (date instanceof Date) {
            return date;
        } else if (typeof date === "string") {
            const [day, month, year] = date.split("/").map(Number);
            return new Date(year, month - 1, day);
        }
        console.error("Invalid date format. Use 'DD/MM/YYYY' or a Date object.");
    }

    getBirthDateAsString() {
        if (this.birth_date) {
            return convertDate(this.birth_date);
        } else {
            return "";
        }
    }

    setBreedName(name){
        this.#breed_name = name;
    }

    handleChange(name, value) {
        const object = this;
        switch (name) {
            case 'petName': object.name = value; break;
            case 'chip' : object.chip = value; break;
            case 'specie': object.specie_id = value; break;
            case 'breed': object.setBreedName(value); break;
            case 'date' : object.birth_date = value; break;
            case 'sex': object.sex_id = value; break;
            case 'sterilisation': object.sterilisation_id = value; break;
            default: console.warn(`handleChange: Clé "${name}" non reconnue.`); break;
        }
        return object;
    }

}

//Classe générique représentant une analyse

//Classe représentant une vraie ananlyse

//Classe représentant un "faux" bilan

//Classe représentant un "vrai" bilan
